import { Box, Link, SxProps, Typography } from '@mui/material';
import KeyboardArrowRightOutlinedIcon from '@mui/icons-material/KeyboardArrowRightOutlined';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CompanyServices } from '../../common/models/user.models';
import Sheet from '../../common/components/Sheet';
import { Service  } from '../services/state';
import ServiceCard from '../services/ServiceCard';
import { getSortedServiceConfigurationsByStatus } from '../services/services.utils';

export interface CompanyServicesInfoLightProps {
  services: CompanyServices;
  sx?: SxProps;
}

export default function CompanyServicesInfoLight({ services, sx }: CompanyServicesInfoLightProps): React.JSX.Element {
  const { t } = useTranslation(['common', 'company', 'settings']);
  const [isServicesSheetOpen, setIsServicesSheetOpen] = useState(false);

  const [enabledServiceConfigurations, disabledServiceConfigurations] = getSortedServiceConfigurationsByStatus(services);
  const sortedServices = enabledServiceConfigurations.concat(disabledServiceConfigurations);

  const getServiceStatusLabel = (service: Service): string => {
    const status = services[service];
    let label: string;

    if (status === null) {
      label = `🤔 ${t('common:to_confirm')}`;
    } else if (status) {
      label = `✅ ${t('settings:yes')}`;
    } else {
      label = `❌ ${t('settings:no')}`;
    }

    return label;
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'surfaceContainerLow.main',
          position: 'relative',
          p: 2,
          pb: 0,
          borderRadius: 3,
          gap: 0.5,
          overflow: 'hidden',
          ...sx,
        }}
      >
        <Typography variant="titleSmall">
          {t('company:services_to_buyers')}
        </Typography>
        {sortedServices.map(({ wordingNamespace, service }) => (
          <Box sx={{ display: 'flex' }} key={service}>
            <Typography variant="bodySmall" sx={{ flex: 1 }}>
              {t(`settings:${wordingNamespace}.title`)}
            </Typography>
            <Typography variant="bodySmall">
              {getServiceStatusLabel(service)}
            </Typography>
          </Box>
        ))}
        <Box
          className="Kheops-CompanyServicesInfoLight-toggle"
          sx={{
            backgroundColor: 'inherit',
            width: 'calc(100% - 32px)',
            position: 'absolute',
            bottom: 0,
            pb: 2,
            pt: 1,
          }}
        >
          <Link
            sx={{ display: 'flex', alignItems: 'center', color: 'onSurfaceVariant.main', cursor: 'pointer' }}
            onClick={() => setIsServicesSheetOpen(true)}
          >
            <KeyboardArrowRightOutlinedIcon sx={{ fontSize: '1.125rem', mr: 0.5 }} />
            <Typography variant="labelSmall">
              {
                enabledServiceConfigurations.length > 3
                  ? t('common:see_N_more', { count: 5 - enabledServiceConfigurations.length })
                  : t('common:see_more')
              }
            </Typography>
          </Link>
        </Box>
      </Box>
      <Sheet
        open={isServicesSheetOpen}
        onClose={() => setIsServicesSheetOpen(false)}
        width={368}
        title={t('company:services_to_buyers')}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            '.Kheops-ServiceCard-card': {
              backgroundColor: 'surfaceContainer.main'
            }
          }}
        >
          {sortedServices.map((configuration) => (
            <ServiceCard
              key={configuration.service}
              configuration={configuration}
              companyServices={services}
              readOnly
              dense
            />
          ))}
        </Box>
      </Sheet>
    </>
  )
}
